export interface PDFGenerateParams {
  template: string;
  config: string;
  landscape?: boolean;
  export?: "pdf" | "image";
  email_copy?: boolean;
}

/** Request body for POST /pdf/generate-async */
export interface SinglePDFAsyncRequest {
  id: string;
  template: string;
  config: string;
  landscape?: string;
  export?: string;
  filename?: string;
}

export type PdfGenerationStage =
  | "requesting"
  | "configuring"
  | "mapping"
  | "generating"
  | "downloading"
  | "complete"
  | "failed";

/**
 * Parameters for generating a single PDF in a batch
 */
export interface PDFGenerationParams {
  id: string;
  template?: string;
  config?: string;
  landscape?: string;
  export?: string;
}

/**
 * Request for batch PDF generation
 */
export interface BatchPDFRequest {
  pdfs: PDFGenerationParams[];
  filename?: string;
  include_manifest?: boolean;
}

/**
 * Progress state for batch PDF generation
 */
export interface BatchPDFProgress {
  phase: "preparing" | "generating" | "downloading" | "complete" | "error";
  message: string;
  progress?: number;
}

/**
 * Result of batch PDF generation
 */
export interface BatchPDFResult {
  success: boolean;
  fileType: "pdf" | "zip";
  filename: string;
  documentCount: number;
  failedCount?: number;
  generationTime?: string;
}

/**
 * Response from starting an async batch PDF generation task
 */
export interface BatchPDFAsyncStartResponse {
  task_id: string;
  status: string;
  message: string;
  created_at: string;
}

/**
 * Details object containing the download URL and metadata
 */
export interface BatchPDFResultDetails {
  blob_name?: string;
  container_name?: string;
  /** Legacy tasks only — stripped from API responses for new tasks */
  download_url?: string;
  filename: string;
  total_pdfs: number;
  successful_pdfs: number;
  failed_pdfs: number;
  zip_size_bytes: number;
  generation_time_seconds: number;
}

/**
 * Status response for an async batch PDF generation task
 * Supports both old format (flat result) and new format (nested details)
 */
export type BatchPDFTaskStatus =
  | "pending"
  | "queued"
  | "processing"
  | "completed"
  | "failed"
  | "cancelled";

export function isActivePdfBatchTaskStatus(status: string): boolean {
  return status === "pending" || status === "queued" || status === "processing";
}

export function isCompletedPdfBatchTaskStatus(status: string): boolean {
  const normalised = status.toLowerCase();
  return (
    normalised === "completed" ||
    normalised === "success" ||
    normalised === "partial_failure"
  );
}

export function getPdfBatchTaskStatusLabel(status: string): string {
  switch (status) {
    case "completed":
    case "success":
    case "partial_failure":
      return "Completed";
    case "failed":
      return "Failed";
    case "processing":
      return "Processing";
    case "pending":
      return "Pending";
    case "queued":
      return "Queued";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
}

export interface BatchPDFAsyncStatusResponse {
  task_id: string;
  status: BatchPDFTaskStatus;
  total_pdfs: number;
  completed_pdfs: number;
  failed_pdfs: number;
  progress_percentage: number;
  stage?: string | null;
  status_message?: string | null;
  steps?: import("@/services/api/data-hub/types").TaskStep[];
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  error: string | null;
  result: {
    /** New format: nested under details */
    details?: BatchPDFResultDetails;
    /** Old format: flat structure (backward compatibility) */
    download_url?: string;
    filename?: string;
    total_pdfs?: number;
    successful_pdfs?: number;
    failed_pdfs?: number;
    zip_size_bytes?: number;
    generation_time_seconds?: number;
  } | null;
}

/**
 * Task list item (subset of full status)
 * Supports both old format (flat result) and new format (nested details)
 */
export interface BatchPDFTaskListItem {
  task_id: string;
  status: BatchPDFTaskStatus;
  filename: string;
  total_pdfs: number;
  completed_pdfs: number;
  failed_pdfs: number;
  progress_percentage: number;
  created_at: string;
  updated_at: string;
  completed_at?: string;
    result?: {
    /** New format: nested under details */
    details?: {
      blob_name?: string;
      container_name?: string;
      download_url?: string;
      filename: string;
      successful_pdfs: number;
    };
    /** Old format: flat structure (backward compatibility) */
    download_url?: string;
    filename?: string;
    successful_pdfs?: number;
  };
}

/**
 * Response from list tasks endpoint
 */
export interface BatchPDFTaskListResponse {
  tasks: BatchPDFTaskListItem[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Helper to extract result details from either old or new API format
 * Handles backward compatibility between flat result and nested details structure
 * 
 * @param result - The result object from API response
 * @returns Normalized result details or null if not available
 */
export function getResultDetails(result: BatchPDFAsyncStatusResponse['result'] | BatchPDFTaskListItem['result']): {
  blob_name?: string;
  container_name?: string;
  download_url?: string;
  filename: string;
  successful_pdfs: number;
  failed_pdfs?: number;
  generation_time_seconds?: number;
  zip_size_bytes?: number;
  total_pdfs?: number;
} | null {
  if (!result) return null;

  const normalize = (details: {
    blob_name?: string;
    container_name?: string;
    download_url?: string;
    filename?: string;
    successful_pdfs?: number;
    failed_pdfs?: number;
    generation_time_seconds?: number;
    zip_size_bytes?: number;
    total_pdfs?: number;
  }) => ({
    blob_name: details.blob_name,
    container_name: details.container_name,
    download_url: details.download_url,
    filename: details.filename || "",
    successful_pdfs: details.successful_pdfs || 0,
    failed_pdfs: details.failed_pdfs,
    generation_time_seconds: details.generation_time_seconds,
    zip_size_bytes: details.zip_size_bytes,
    total_pdfs: details.total_pdfs,
  });

  const asRecord = result as {
    blob_name?: string;
    container_name?: string;
    download_url?: string;
    filename?: string;
    successful_pdfs?: number;
    failed_pdfs?: number;
    generation_time_seconds?: number;
    zip_size_bytes?: number;
    total_pdfs?: number;
    details?: Record<string, unknown>;
  };

  const nested = asRecord.details;
  const nestedInner =
    nested && typeof nested.details === "object" && nested.details !== null
      ? (nested.details as Record<string, unknown>)
      : null;
  const payload = (nestedInner || nested || asRecord) as {
    blob_name?: string;
    container_name?: string;
    download_url?: string;
    filename?: string;
    successful_pdfs?: number;
    failed_pdfs?: number;
    generation_time_seconds?: number;
    zip_size_bytes?: number;
    total_pdfs?: number;
  };

  if (payload.blob_name || payload.download_url || payload.filename) {
    return normalize(payload);
  }

  return null;
}

/** Whether a completed task has a file available for download (new or legacy format). */
export function taskHasDownloadableFile(
  result: BatchPDFAsyncStatusResponse["result"] | BatchPDFTaskListItem["result"],
): boolean {
  const details = getResultDetails(result);
  return !!details && (!!details.blob_name || !!details.download_url || !!details.filename);
}
